const wcmatch = require('wildcard-match');

const GLOB_PATTERN_SEPARATOR = '.';

module.exports.rolesToGraph = function rolesToGraph(roles) {
    const graph = {};

    roles.forEach(role => {
        const { name, context } = role;
        graph[name] = {
            name,
            context: context ? context.map(_ => _) : context,
            inherits: [],
        };
    });

    roles.forEach(role => {
        const { name, inherits } = role;
        for (let i = 0; i < inherits.length; i++) {
            graph[name].inherits[i] = graph[inherits[i]];
        }
    });

    return graph;
};

module.exports.addAllRedundantEdges = function addAllRedundantEdges(graph, graphReachableFrom = {}, nodeName) {
    if (nodeName) {
        const node = graph[nodeName];

        let nodeReachableFrom = {};

        for (let { name: nodePointer } of node.inherits) {
            nodeReachableFrom[nodePointer] = true;

            if (!graphReachableFrom[nodePointer]) {
                addAllRedundantEdges(graph, graphReachableFrom, nodePointer);
            }
            for (let subNode of graphReachableFrom[nodePointer]) {
                nodeReachableFrom[subNode] = true;
            }
        }

        graphReachableFrom[nodeName] = Object.keys(nodeReachableFrom);
    } else {
        for (let nodePointer in graph) {
            if (!graphReachableFrom[nodePointer]) {
                addAllRedundantEdges(graph, graphReachableFrom, nodePointer);
            }
        }

        for (let nodePointer in graph) {
            const node = graph[nodePointer];
            node.inherits = graphReachableFrom[nodePointer].map(nodeName => graph[nodeName]);
        }
    }
};

function addInverseGraph(completeDag) {
    for (let nodeName in completeDag) {
        const targetNode = completeDag[nodeName];
        for (let sourceNode of targetNode.inherits) {
            if (!sourceNode.parents) {
                sourceNode.parents = [];
            }

            if (!sourceNode.parents.includes(targetNode)) {
                sourceNode.parents.push(targetNode);
            }
        }
    }
}

function completePermissionRequiresWithInheritedPermissions(completeDag, permissions) {
    for (let permission of permissions) {

        const requiresBoolMap = permission.requires.reduce(function (acc, roleName) {
            acc[roleName] = true;
            return acc;
        }, {});

        for (let roleName of permission.requires) {
            const role = completeDag[roleName];
            for (let { name: inheritedRoleName } of (role.parents || [])) {
                requiresBoolMap[inheritedRoleName] = true;
            }
        }

        permission.requires = Object.keys(requiresBoolMap);
    }
}

module.exports.rolesToPermissionsMatchers = function rolesToPermissionsMatchers(completeDag, permissions) {
    addInverseGraph(completeDag);
    completePermissionRequiresWithInheritedPermissions(completeDag, permissions);

    const rolesToPermissionsMatchers = {};

    permissions.forEach(permission => {
        permission.requires.forEach(role => {
            if (!rolesToPermissionsMatchers[role]) {
                rolesToPermissionsMatchers[role] = [];
            }

            rolesToPermissionsMatchers[role].push(createGlobMatcher(permission.name));
        });
    });

    return rolesToPermissionsMatchers;
};

function createGlobMatcher(pattern) {
    if (pattern.includes('*')) {
        return wcmatch(pattern, { separator: GLOB_PATTERN_SEPARATOR });
    }

    return (checkedPermission) => pattern === checkedPermission;
}

/**
 * Expands permission groups in the permissions array into individual regular permissions.
 * Each pattern in a group's "permissions" array becomes a separate permission entry,
 * inheriting the group's "requires" roles.
 *
 * Example:
 * A group like:
 * {
 *   "type": "group",
 *   "name": "_group_example",
 *   "permissions": ["permission.a", "permission.b.*"],
 *   "requires": ["role1", "role2"]
 * }
 *
 * Will be expanded to:
 * [
 *   { "name": "permission.a", "requires": ["role1", "role2"] },
 *   { "name": "permission.b.*", "requires": ["role1", "role2"] }
 * ]
 *
 * @param {Array} permissions - The raw permissions array from permissions.json
 * @return {Array} A new permissions array with groups expanded into regular permissions
 */
module.exports.expandPermissionGroups = function expandPermissionGroups(permissions) {
    const result = [];

    for (const permission of permissions) {
        if (!permission.type || permission.type !== 'group') {
            result.push(permission);
            continue;
        }

        for (const permissionPattern of permission.permissions) {
            result.push({
                name: permissionPattern,
                requires: permission.requires ? [...permission.requires] : []
            });
        }
    }

    return result;
};

module.exports.roleMatch = function roleMatch(userRole, roleDefinition, context) {
    if (userRole.role != roleDefinition.name) {
        return false;
    }
    if (!roleDefinition.context) {
        return true;
    }

    context = context || {};
    for (let attribute of roleDefinition.context) {
        if (!userRole[attribute] || !context[attribute] || userRole[attribute] !== context[attribute]) {
            return false;
        }
    }

    return true;
};


module.exports.transitiveReduction = function transitiveReduction(graph) {
    // Based on Harry Hsu's algorithm "An algorithm for finding a minimal
    // equivalent graph of a digraph"

    // we first generate the corresponding edge matrix
    const rolesIndex = {};
    let latestIndex = -1;
    for (let edgeName in graph) {
        latestIndex++;
        rolesIndex[edgeName] = latestIndex;
    }
    const N = latestIndex + 1;
    const m = [];
    for (let i = 0; i < N; i++) {
        m[i] = [];
        for (let j = 0; j < N; j++) {
            m[i][j] = false;
        }
    }
    for (let edgeName in graph) {
        const edgeTarget = graph[edgeName];
        const edgeTargetIndex = rolesIndex[edgeTarget.name];
        for (let edgeSource of edgeTarget.inherits) {
            const edgeSourceIndex = rolesIndex[edgeSource.name];
            m[edgeSourceIndex][edgeTargetIndex] = true;
        }
    }

    // reflexive reduction
    // (we normally don't need it since we require the graph to be acyclic)
    for (let i = 0; i < N; ++i) {
        m[i][i] = false;
    }

    // transitive reduction
    for (let j = 0; j < N; j++) {
        for (let i = 0; i < N; i++) {
            if (m[i][j]) {
                for (let k = 0; k < N; k++) {
                    if (m[j][k]) {
                        m[i][k] = false;
                    }
                }
            }
        }
    }

    // apply the resulting matrix to our graph data structure
    for (let edgeName in graph) {
        const edgeTarget = graph[edgeName];
        const edgeTargetIndex = rolesIndex[edgeTarget.name];
        for (let i = 0; i < edgeTarget.inherits.length; i++) {
            const edgeSource = edgeTarget.inherits[i];
            const edgeSourceIndex = rolesIndex[edgeSource.name];
            if (!m[edgeSourceIndex][edgeTargetIndex]) {
                edgeTarget.inherits.splice(i, 1);
                i--;
            }
        }
    }
};
